import { ChangeDetectorRef, Component, DestroyRef, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { API } from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { SbDateTime } from '../../../../services/api/api-date-time';
import { DateTime, Duration } from 'luxon';
import { XYChartModel } from '@ngx-seatback/charts';
import { UtilService } from '../../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ChartComponent } from '@ngx-seatback/charts';
import { Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-comp-service-time-data',
  templateUrl: './service-time-data.component.html',
  styleUrls: ['./service-time-data.component.scss'],
  standalone: false,
})
export class ServiceTimeDataComponent implements OnInit, OnDestroy {
  @ViewChild('chart') chart: ChartComponent;
  loading: boolean;
  @Input() resolveInCom: boolean;
  timespan: Duration;
  @Input() halfHeight: boolean;
  serviceData: XYChartModel;
  serviceDataChartData: any;
  $destroy: Subject<any> = new Subject<void>();
  @ViewChild('chartLegend', { read: ElementRef, static: false }) chartLegendContainer: ElementRef;
  subs: Subscription[] = [];

  constructor(
    private cdRef: ChangeDetectorRef,
    private api: API,
    private errorHandler: ErrorService,
    public util: UtilService,
    private translate: TranslateService,
    private destroyRef: DestroyRef
  ) {
    this.loading = true;
    this.timespan = Duration.fromMillis(0);
    this.serviceData = {
      data: undefined,
      height: 0,
      loadingDone: false,
      title: '',
      valueAssignments: [],
    };
    this.serviceDataChartData = [];
  }

  // tslint:disable-next-line:variable-name
  _chartHeight: number;

  get chartHeight() {
    return this._chartHeight;
  }

  @Input() set chartHeight(val) {
    if (val) {
      this._chartHeight = val;
    } else {
      this._chartHeight = 150;
    }
  }

  // tslint:disable-next-line:variable-name
  _inputData: any;

  get inputData() {
    return this._inputData;
  }

  @Input() set inputData(val) {
    this._inputData = val;
    if (!this.resolveInCom && this._inputData) {
      this.loading = false;
      this.cdRef.detectChanges();
      this.initChartData(this._inputData);
    }
  }

  // tslint:disable-next-line:variable-name
  _from?: SbDateTime;

  get from(): SbDateTime {
    return this._from;
  }

  @Input() set from(val) {
    this._from = val;
  }

  // tslint:disable-next-line:variable-name
  _till?: SbDateTime;

  get till(): SbDateTime {
    return this._till;
  }

  @Input() set till(val) {
    this._till = val;
  }

  ngOnInit(): void {
    if (this.resolveInCom) {
      this.refreshFromServer();
    }
    this.translate.onLangChange.pipe(takeUntil(this.$destroy)).subscribe(() => {
      this.buildChart();
    });
  }

  refreshFromServer() {
    if (!this.from && !this.till) {
      this.from = new SbDateTime(DateTime.local().startOf('day').minus({ day: 91 }));
      this.till = new SbDateTime(DateTime.local().startOf('day').minus({ day: 1 }));
    }
    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];
    const sub = this.api
      .ss_nm_Reporting_deviceStatusCountHistory({
        from: this.from,
        till: this.till,
        productive: true,
        interval: 604800,
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: data => {
          this.initChartData(data.buckets[0].frames);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  initChartData(data) {
    if (this.from && this.till) {
      this.timespan = DateTime.fromJSDate(this.util.localDateToJSDate(this.till)).diff(
        DateTime.fromJSDate(this.util.localDateToJSDate(this.from)),
        'days'
      );
    }

    this.serviceDataChartData = [];

    for (const i of data) {
      this.serviceDataChartData.push({
        date: i.range.from.value,
        tickets: i.data.subsetServiceRequested,
        ticketsClosed: i.data.eventServiceRequestResolved,
        newTickets: i.data.eventServiceRequested,
      });
    }
    this.buildChart();
  }

  buildChart() {
    this.translate.get(marker('lang.globals.openTickets')).subscribe(openTickets => {
      this.translate.get(marker('lang.globals.closedTickets')).subscribe(closedTickets => {
        this.translate.get(marker('lang.globals.newTickets')).subscribe(newTickets => {
          this.serviceData = {
            title: 'Test',
            height: this.chartHeight,
            categoryType: 'date',
            legendContainerWidth: '600px',
            valueAssignments: [
              {
                value: 'tickets',
                categories: ['date'],
                valueName: openTickets,
                color: '#c36157',
              },
              {
                value: 'ticketsClosed',
                categories: ['date'],
                valueName: closedTickets,
                color: '#3676c3',
              },
              {
                value: 'newTickets',
                categories: ['date'],
                valueName: newTickets,
                color: '#c3af02',
              },
            ],
            legend: true,
            legendContainer: this.chartLegendContainer,
            legendAlign: 'right',
            loadingDone: true,
            tensionX: 1,
            data: this.serviceDataChartData,
          };
          this.loading = true;
          this.cdRef.detectChanges();
          this.chart.rerenderChart(this.serviceData);
        });
      });
    });
    // console.log(this.chart);
  }

  ngOnDestroy(): void {
    this.$destroy.next(null);
    this.$destroy.complete();
    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];
  }
}
